import { FC, useMemo } from 'react';
import { IComparisonOperator, IRuleProps } from './types';
import { useTranslation } from 'react-i18next';
import PerformEventRuleIcon from '../../../../assets/icons/rule-perform-event.svg?react';
import VisitedPageRuleIcon from '../../../../assets/icons/rule-visit-page.svg?react';
import { EventValue, RuleValue } from '.';

export const ActionRule: FC<IRuleProps> = ({ expression }) => {
  const { t } = useTranslation('audience');
  const COMPARISON_OPERATOR: ReadonlyArray<IComparisonOperator> = useMemo(
    () => [
      {
        id: 'AT_LEAST',
        label: t('rules.at_least'),
      },
      {
        id: 'EXACTLY',
        label: t('rules.exactly'),
      },
      {
        id: 'AT_MOST',
        label: t('rules.at_most'),
      },
      {
        id: 'NEVER',
        label: t('rules.no_times'),
      },
    ],
    []
  );

  const TEMPORAL_HORIZON_OPERATOR: ReadonlyArray<IComparisonOperator> = useMemo(
    () => [
      {
        id: 'WITHIN',
        label: t('rules.within'),
      },
      {
        id: 'BETWEEN',
        label: t('rules.in_between'),
      },
    ],
    []
  );

  const TEMPORAL_HORIZON_UNITS: ReadonlyArray<IComparisonOperator> = useMemo(
    () => [
      {
        id: 'DAY',
        label: t('rules.days'),
      },
      {
        id: 'MONTH',
        label: t('rules.months'),
      },
      {
        id: 'YEAR',
        label: t('rules.years'),
      },
    ],
    []
  );

  const comparisonOperator = useMemo(() => COMPARISON_OPERATOR.find((o) => expression.occurrences.comparisonOp === o.id), [expression]);
  const occurrencyTimes = useMemo(() => expression.occurrences.times, [expression]);

  const temporalHorizonOperator = useMemo(() => TEMPORAL_HORIZON_OPERATOR.find((o) => expression.temporalHorizon?.type === o.id), [expression]);
  const temporalHorizonUnit = useMemo(() => TEMPORAL_HORIZON_UNITS.find((o) => expression.temporalHorizon?.unit === o.id), [expression]);

  const renderRuleIcon = () => {
    switch (expression.kind) {
      case 'VISIT':
        return <VisitedPageRuleIcon className='text-brand-500' />;
      case 'EVENT':
      default:
        return <PerformEventRuleIcon className='text-brand-500' />;
    }
  };

  const renderTemporalRange = () => {
    switch (expression.temporalHorizon.type) {
      case 'BETWEEN':
        return (
          <>
            <RuleValue>{expression.temporalHorizon.from}</RuleValue>
            <p className='text-xs font-medium text-tertiary-600'>-</p>
            <RuleValue>{expression.temporalHorizon.to}</RuleValue>
          </>
        );
      case 'WITHIN':
      default:
        return (
          <>
            <RuleValue>{expression.temporalHorizon.value}</RuleValue>
            <p className='text-xs font-medium text-tertiary-600'>{temporalHorizonUnit.label}</p>
          </>
        );
    }
  };

  return (
    <div className='flex flex-col gap-1.5'>
      <div className='flex gap-2 text-sm font-medium text-tertiary-600'>
        {renderRuleIcon()}
        <EventValue ruleId={expression.dataSourceRuleId} type={expression.kind === 'EVENT' ? 'TRACK' : 'VIEW'} expressionKind={expression.kind} />
      </div>
      <div className='flex flex-col gap-2'>
        {comparisonOperator.id === 'NEVER' ? (
          <RuleValue>{comparisonOperator.label}</RuleValue>
        ) : (
          <>
            <div className='flex items-center gap-2'>
              <p className='text-xs font-medium text-tertiary-600'>{comparisonOperator.label}</p>
              <RuleValue>
                {occurrencyTimes} {t('rules.times')}
              </RuleValue>
            </div>
            <div className='flex items-center gap-2'>
              <p className='text-xs font-medium text-tertiary-600'>{temporalHorizonOperator.label}</p>
              {renderTemporalRange()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
